import { z } from 'zod';
import {
  AnalysisLensSchema,
  ConfidenceSchema,
  DomainSchema,
  type FindingState,
  FindingStateSchema,
  PredicateBasisSchema,
  ProseOriginSchema,
  ScoringMethodSchema,
  SeveritySchema,
  StrideCategorySchema,
} from './enums';
import {
  AssessmentIdSchema,
  AssumptionIdSchema,
  EvidenceRefSchema,
  FindingIdSchema,
  PackIdSchema,
  RunIdSchema,
  TenantIdSchema,
  ThreatPatternIdSchema,
  UnknownIdSchema,
} from './ids';
import { NonEmptyStringSchema, SemVerSchema, TimestampSchema } from './primitives';
import { RiskDimensionsSchema } from './scoring';
import { FindingStatementSchema } from './statement';

/** A finding that only applies if some condition holds (DESIGN_SPEC §8.3). */
const ConditionalShape = z.object({
  is_conditional: z.boolean(),
  condition: z.string().nullable(),
});

export const ConditionalSchema = ConditionalShape.superRefine((value, ctx) => {
  if (value.is_conditional && (value.condition === null || value.condition.length === 0)) {
    ctx.addIssue({
      code: 'custom',
      message: 'a conditional finding must state its condition',
      path: ['condition'],
    });
  }
});
export type Conditional = z.infer<typeof ConditionalShape>;

/**
 * What a domain worker is allowed to emit.
 *
 * This is a **strict** object, and the omissions are the point:
 *
 *  - no `confidence` — it is derived from structural signals in code (§8.4,
 *    CLAUDE.md principle 5). A model that tries to report its own confidence
 *    has its output rejected outright rather than quietly ignored.
 *  - no `severity_normalised`, `scoring_method` or `scoring_detail` — scoring is
 *    a separate, pluggable, deterministic stage (§9).
 *  - no `tenant_id`, `assessment_id` or `finding_id` — those are injected by the
 *    orchestrator from the run context, so a worker cannot address another
 *    tenant even in principle.
 *  - no `analysis_lens` or `stride_category` — both are inherited from the
 *    resolved pattern in `validateFinding` (docs/DECISIONS.md ZD-009). A draft
 *    has no field through which it could assert either, so there is nothing
 *    to cross-check against the pattern's anchor.
 */
export const CandidateFindingDraftSchema = z.strictObject({
  domain: DomainSchema,
  title: NonEmptyStringSchema,
  description: NonEmptyStringSchema,

  /** Anchor into a pack: stable identity, and the unit reproducibility counts. */
  threat_pattern_id: ThreatPatternIdSchema,

  /** Grounding: at least one real canonical object id (CLAUDE.md principle 4). */
  grounded_object_ids: z.array(z.string()).min(1),

  conditional: ConditionalSchema,
  evidence_refs: z.array(EvidenceRefSchema),

  /** Why this pattern applies to *this* system — the worker's contextualisation. */
  rationale: NonEmptyStringSchema,
});
export type CandidateFindingDraft = z.infer<typeof CandidateFindingDraftSchema>;

/**
 * One attribute a finding rests on, and how confident we are in it
 * (docs/DECISIONS.md ZD-020).
 *
 * The confidence is computed by `attributeConfidence` from the attribute's own
 * provenance — it is recorded here so the evidence pack can show the working,
 * not so anything can assert it.
 */
export const AttributeDependencySchema = z.object({
  object_id: z.string(),
  attribute: NonEmptyStringSchema,
  confidence: ConfidenceSchema,
});
export type AttributeDependency = z.infer<typeof AttributeDependencySchema>;

/**
 * The structural inputs that produced `confidence`, retained so the derivation
 * is auditable. "Confidence is derived, not self-reported" is only a meaningful
 * claim if the evidence pack can show the working.
 */
export const ConfidenceBasisSchema = z.object({
  predicate_basis: PredicateBasisSchema,
  /** How sure we are the grounded objects exist at all. */
  grounded_object_confidences: z.array(ConfidenceSchema),
  /**
   * The attributes this finding actually depends on (docs/DECISIONS.md ZD-020).
   *
   * Supplied by the pack service from the pattern's applicability predicate
   * (sds-04). Until that exists, `validateFinding` fills it conservatively with
   * every separately-established attribute of every grounded object: not
   * knowing which attributes matter is not a reason to assume the contradicted
   * one does not.
   */
  depends_on_attributes: z.array(AttributeDependencySchema),
  depends_on_assumption_ids: z.array(AssumptionIdSchema),
  depends_on_unknown_ids: z.array(UnknownIdSchema),
});
export type ConfidenceBasis = z.infer<typeof ConfidenceBasisSchema>;

/** A persisted finding (DESIGN_SPEC §7.6). */
const CandidateFindingShape = z.object({
  finding_id: FindingIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,

  /** The agent run that produced it; null for findings created by hand. */
  run_id: RunIdSchema.nullable(),

  state: FindingStateSchema,
  domain: DomainSchema,
  /**
   * `analysis_lens` and `stride_category` are both inherited from the resolved
   * pattern's anchor (docs/DECISIONS.md ZD-009) — a worker never chooses either,
   * and the draft schema carries no field that could assert its own value.
   */
  analysis_lens: AnalysisLensSchema,
  stride_category: StrideCategorySchema.nullable(),
  title: NonEmptyStringSchema,
  description: NonEmptyStringSchema,
  rationale: NonEmptyStringSchema,

  /**
   * Whether the prose above was **rendered from the finding's own witnesses**
   * or **authored by a model** (docs/DECISIONS.md ZD-031).
   *
   * A rendered rationale is a transcript: every clause of it is a fact in the
   * canonical model with a citation, so it asserts nothing the model does not
   * hold. An authored one is contextualisation, which is the LLM worker's whole
   * job and is a different kind of claim. Without this field sds-09 cannot tell
   * which population it is scoring — and, the reason it is a field rather than
   * a convention, the LLM half could replace a transcript with editorial text
   * and nothing would show.
   *
   * Set by `validateFinding` from the run context, never from the draft: a
   * worker has no field through which to claim its own prose is a transcript.
   */
  prose_origin: ProseOriginSchema,

  threat_pattern_id: ThreatPatternIdSchema,

  /**
   * The pack version that supplied the anchor. Pinned on the assessment too
   * (§12.2), but carried here so drift analysis can work finding-by-finding
   * rather than run-by-run.
   */
  pack_id: PackIdSchema,
  pack_version: SemVerSchema,

  grounded_object_ids: z.array(z.string()).min(1),

  confidence: ConfidenceSchema,
  confidence_basis: ConfidenceBasisSchema,

  conditional: ConditionalSchema,

  severity_normalised: SeveritySchema,
  scoring_method: ScoringMethodSchema,
  scoring_detail: z.record(z.string(), z.unknown()),
  risk_dimensions: RiskDimensionsSchema,

  evidence_refs: z.array(EvidenceRefSchema),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type CandidateFinding = z.infer<typeof CandidateFindingShape>;

/**
 * A STRIDE pattern without a category is as wrong as a non-STRIDE pattern
 * carrying one (docs/DECISIONS.md ZD-009) — both directions are enforced.
 *
 * `validateFinding` cannot produce a mismatch (it inherits both fields from
 * the resolved pattern), but this schema is also what a hand-created finding
 * or an approver edit will be validated against, and neither path exists yet.
 * The invariant belongs on the persisted shape, not only on one writer of it.
 */
export const CandidateFindingSchema = CandidateFindingShape.superRefine((finding, ctx) => {
  if (finding.analysis_lens === 'stride' && finding.stride_category === null) {
    ctx.addIssue({
      code: 'custom',
      message: 'a STRIDE-lens pattern must carry a stride_category',
      path: ['stride_category'],
    });
  }
  if (finding.analysis_lens !== 'stride' && finding.stride_category !== null) {
    ctx.addIssue({
      code: 'custom',
      message: `stride_category must be null for a ${finding.analysis_lens} lens pattern`,
      path: ['stride_category'],
    });
  }
});

/**
 * The reproducibility tuple (CLAUDE.md principle 6): two runs of the same input
 * are compared on `(threat_pattern_id, grounded_object_ids, domain)`, never on
 * wording. Grounded ids are sorted so ordering differences do not read as drift.
 *
 * **This is one layer of the metric, not the whole of it** (ZD-046 §4). It is
 * the *grounding* layer, and read alone it cannot say whether a disagreement
 * came from the analysis or from the extraction underneath it — because ZD-028
 * makes an object id a digest of its name, so one wording difference upstream
 * moves every tuple grounding on that object at once. `./reproducibility`
 * splits it and gates the right layer; do not build a gate on this function by
 * itself.
 */
export function reproducibilityKey(
  finding: Pick<CandidateFinding, 'threat_pattern_id' | 'grounded_object_ids' | 'domain'>,
): string {
  const ids = [...finding.grounded_object_ids].sort().join(',');
  return `${finding.domain}|${finding.threat_pattern_id}|${ids}`;
}

/**
 * A finding as it is persisted — the finding and the statement it was rendered
 * from, together (docs/DECISIONS.md ZD-061).
 *
 * **Both, because either alone is incomplete for the reader that comes next.**
 * `runDomainAnalysis` built a `FindingStatement` for every match and discarded
 * it, so the report assembler and the reviewer had to be handed a live pipeline
 * rather than storage. The statement is also where an approver edit lands
 * (`Slot.authored_text`), so a finding stored without it would have nowhere to
 * put what a reviewer wrote.
 *
 * The payload lives in tenant-encrypted R2 (ZD-008); the `findings` row holds
 * only queryable metadata and a `storage_ref`.
 */
export const PersistedFindingSchema = z.object({
  finding: CandidateFindingSchema,
  statement: FindingStatementSchema,
});
export type PersistedFinding = z.infer<typeof PersistedFindingSchema>;

/** Findings a report may be generated from — and only these (§14.2). */
export const REPORTABLE_FINDING_STATES = ['client_ready'] as const;

/**
 * States that count as an approver having decided (guard on the human gate).
 *
 * **`deferred` is deliberately not among them** (docs/DECISIONS.md ZD-061 §1).
 * Deferring is a real reviewer action — 50 of the corpus's 102 findings are
 * conditional on a fact only the client can supply, and 42 of those would be
 * *settled* by one answer — so a gate demanding one of these four for every
 * finding would manufacture 50 decisions rather than record any. A deferred
 * finding therefore carries a decision and still refuses the transition, and
 * `approver_decided` says which of the two it is refusing on.
 */
export const DECIDED_FINDING_STATES = ['approved', 'edited', 'rejected', 'accepted_risk'] as const;

/**
 * States meaning **a reviewer has recorded a decision**, of which `deferred` is
 * the one that does not release the finding either way.
 *
 * The distinction between this and `DECIDED_FINDING_STATES` is what makes the
 * `approver_decided` guard's deferral clause reachable at all. Without it a
 * deferred finding fails the *undecided* clause first, the deferral clause can
 * never fire, and its mutation reports `STAYED GREEN` — the redundant-twin
 * masking ZD-048 §7 describes, arriving inside the guard that was written to
 * separate the two answers. It was written that way first and the arithmetic
 * caught it before the mutation did.
 *
 * The two lists answer different questions and both are needed: *has this
 * reviewer looked at this finding?* and *may it be reported?*
 */
export const REVIEWED_FINDING_STATES = [...DECIDED_FINDING_STATES, 'deferred'] as const;

/**
 * States a finding is promoted to `client_ready` from at report time
 * (DESIGN_SPEC §5.2, docs/DECISIONS.md ZD-061 §1).
 *
 * **`accepted_risk` releases**, and it is the member that would be dropped by
 * anyone reading the four decided states as a flat list. It is not a sibling of
 * `rejected`: it is an *accept* carrying a treatment. The reviewer judged the
 * threat true of the client's system and chose not to remediate it, and a risk
 * register that omits the risks a client has accepted is a false picture of
 * their system — the acceptance is what `Action.status` carries onward
 * (DESIGN_SPEC §7.8).
 *
 * `deferred` never releases: a finding waiting on a client answer has not been
 * judged either way, so putting it in front of a client asserts something
 * nobody has decided.
 */
export const RELEASED_FINDING_STATES = ['approved', 'edited', 'accepted_risk'] as const;

/**
 * The two states the automated QA review may leave a finding in
 * (DESIGN_SPEC §6.5, §11.1, docs/DECISIONS.md ZD-080).
 *
 * **These are the only members of `FINDING_STATES` nothing produced.** A
 * repository-wide search for `qa_flagged` and `review_required` found them in
 * the enum that declares them and nowhere else, while `validateFinding` writes
 * `candidate` and `findingStateFor` derives only the five post-decision states
 * — so all 102 corpus findings sat at `candidate` from analysis to the gate.
 * That absence is the whole reason the QA stage survives ZD-060's test: of
 * §6.5's eight checks, **six have a producer and every one of the six is
 * upstream**, so the stage earns its place on a state transition rather than on
 * a check (ZD-080 §1).
 *
 * **Neither is a decision**, which is why they are not in
 * `REVIEWED_FINDING_STATES`: a pre-filter produces `review_required` and never
 * `approved` (§6.5's own closing sentence, and `promote_finding_to_approved` is
 * a forbidden action on `QA_REVIEW_CONTRACT`). `qa_flagged` says a check
 * refused something a client would have read, not that the finding is wrong.
 */
export const QA_REVIEW_STATES = [
  'review_required',
  'qa_flagged',
] as const satisfies readonly FindingState[];
export const QaReviewStateSchema = z.enum(QA_REVIEW_STATES);
export type QaReviewState = z.infer<typeof QaReviewStateSchema>;
